/**
 * Documents — the title scan, the VIN photograph, the receipt.
 *
 * ── The bytes come through OUR gate, not core's ──────────────────────────────
 *
 * Every file link points at `GET /api/documents/:id/file`, never at core's
 * `GET /api/files/:id`. Core's route serves any private upload to anyone with
 * a session, including a `user`-role account and a delegate; this application's
 * route requires `users.read` and answers 404 to everyone else. A title scan is
 * not the same thing as a product photograph, and the difference is one URL.
 *
 * ── Sensitive is labelled, not hidden ────────────────────────────────────────
 *
 * A document marked sensitive still appears — hiding it would leave somebody
 * hunting for a title they know they uploaded — but it says so, so nobody
 * opens it on a phone they are showing to a seller.
 */

import { useCallback } from 'react'
import Icon from '../Icon'
import { useLocale, type Locale } from '../../lib/i18n'
import { dateText, TONES } from '../../lib/format'
import { docTypeLabel } from '../../lib/domain'
import { Chip, Empty, Panel, S } from './ui'
import type { DocumentRow } from './types'

const EN = {
  title: 'Documents',
  none: 'Nothing has been filed yet.',
  unavailable: 'Documents could not be loaded.',
  sensitive: 'Sensitive',
  open: 'Open',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Documentos',
  none: 'Todavía no se ha guardado nada.',
  unavailable: 'No se pudieron cargar los documentos.',
  sensitive: 'Delicado',
  open: 'Abrir',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

/** kB or MB, one decimal. Bytes on a screen are noise. */
function size(bytes: number | null, locale: Locale): string {
  if (bytes === null || !Number.isFinite(bytes)) return ''
  const mb = bytes / 1_048_576
  const value = mb >= 1 ? mb : bytes / 1024
  const unit = mb >= 1 ? 'MB' : 'kB'
  return `${new Intl.NumberFormat(locale === 'es-PR' ? 'es-PR' : 'en-US', { maximumFractionDigits: 1 }).format(value)} ${unit}`
}

export default function DocumentsPanel({
  documents,
  failed = false,
}: {
  documents: DocumentRow[]
  failed?: boolean
}) {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])

  return (
    <Panel id="documents" title={t('title')} badge={<Chip tone={TONES.quiet}>{documents.length}</Chip>}>
      {failed ? (
        <Empty>{t('unavailable')}</Empty>
      ) : documents.length === 0 ? (
        <Empty>{t('none')}</Empty>
      ) : (
        documents.map((doc, index) => (
          <div
            key={doc.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '9px 0',
              borderTop: index === 0 ? 'none' : '1px solid var(--line)',
            }}
          >
            <Icon name="ticket" size={18} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>
                {doc.label ?? docTypeLabel(doc.doc_type, locale)}
              </span>
              <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)' }}>
                {[docTypeLabel(doc.doc_type, locale), dateText(doc.captured_on ?? doc.created_at, locale), size(doc.size_bytes, locale)]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              {doc.is_sensitive === 1 && (
                <span style={{ ...S.chipRow, marginTop: 4 }}>
                  <Chip tone={TONES.warn}>{t('sensitive')}</Chip>
                </span>
              )}
            </span>
            <a
              href={`/api/documents/${encodeURIComponent(doc.id)}/file`}
              target="_blank"
              rel="noreferrer"
              style={{ ...S.button, minHeight: 40, textDecoration: 'none' }}
            >
              {t('open')}
            </a>
          </div>
        ))
      )}
    </Panel>
  )
}
